import { Group } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';

/**
 * Create island group container
 */
export const createIslandGroupCfn = (
	_self: KitchenSceneBuilder,
	{ island, index }: { island: any; index: number }
): Group => {
	const group = new Group();

	// Use origin for position and axis.y for rotation
	const origin = island.origin ?? { x: 0, y: 0, z: 0 };
	const axisY = island.axis?.y ?? 0;

	// Detect NaN values in origin and use fallback (0) if found
	// NaN origins occur when island positions are calculated with invalid material keys
	// (e.g., invalid surround key causes getCenterOffsets to return NaN)
	let posX = origin.x;
	let posZ = origin.z;
	let rotY = (-axisY * Math.PI) / 180;

	if (isNaN(posX) || isNaN(posZ)) {
		console.warn(`KitchenSceneBuilder: Island "${island.id ?? index}" has NaN origin (x=${origin.x}, z=${origin.z}). Using fallback position (0, 0). This may be caused by invalid material keys.`);
		posX = isNaN(posX) ? 0 : posX;
		posZ = isNaN(posZ) ? 0 : posZ;
	}

	if (isNaN(rotY)) {
		console.warn(`KitchenSceneBuilder: Island "${island.id ?? index}" has NaN rotation (axisY=${axisY}). Using fallback rotation (0).`);
		rotY = 0;
	}

	group.position.set(posX, 0, posZ);
	group.rotation.y = rotY;

	group.userData = {
		id: island.id ?? `island-${index}`,
		type: 'island',
		index,
		// Center in LOCAL coordinates (relative to island group origin)
		// Used by flyFromPresentationPoint animation as the starting point
		center: { x: 0, y: 0, z: 0 },
		// Flag if this island had NaN origin (useful for debugging)
		hadNaNOrigin: isNaN(origin.x) || isNaN(origin.z)
	};

	group.name = island.id ?? `island-${index}`;

	return group;
};

/**
 * Find the index of the default island using the defaultIslandId from the kitchen data.
 * Falls back to index 0 if no default is found.
 */
export const findDefaultIslandIndexCfn = (
	self: KitchenSceneBuilder,
	{ islands }: { islands: any[] }
): number => {
	if (islands.length === 0) {
		return 0;
	}

	// Get the default island ID from the kitchen data manager
	const defaultIslandId = self.kitchenDataManagerAccess.getDefaultIslandId();

	if (defaultIslandId) {
		// Find island with matching ID
		for (let i = 0; i < islands.length; i++) {
			if (islands[i].id === defaultIslandId) {
				return i;
			}
		}
	}

	// Fallback: check for isDefault flag on individual islands
	for (let i = 0; i < islands.length; i++) {
		if (islands[i].isDefault === true) {
			return i;
		}
	}

	// Fallback to first island if no default found
	return 0;
};

/**
 * Get island indices in render order: default first, then radiating outward by position.
 * Wave 0: Default island
 * Wave 1: Islands immediately adjacent to default (one on each side)
 * Wave 2: Next islands outward on each side
 * etc.
 * Returns array of { index, wave } where wave groups islands that animate together.
 */
export const getIslandRenderOrderCfn = (
	_self: KitchenSceneBuilder,
	{ islands, defaultIndex }: { islands: any[]; defaultIndex: number }
): Array<{ index: number; wave: number }> => {
	const result: Array<{ index: number; wave: number }> = [];

	if (islands.length === 0) {
		return result;
	}

	// Create array of island indices with their X positions
	const islandPositions: Array<{ index: number; x: number }> = islands.map((island, i) => ({
		index: i,
		x: island.origin?.x ?? 0
	}));

	// Sort by X position (left to right)
	islandPositions.sort((a, b) => a.x - b.x);

	// Find where the default island is in the sorted order
	const sortedDefaultIdx = islandPositions.findIndex(ip => ip.index === defaultIndex);

	// Wave 0: default island
	result.push({ index: defaultIndex, wave: 0 });

	// Radiate outward from default position
	let left = sortedDefaultIdx - 1;  // Index in sorted array, to the left of default
	let right = sortedDefaultIdx + 1; // Index in sorted array, to the right of default
	let wave = 1;

	while (left >= 0 || right < islandPositions.length) {
		// Add island to the left (if any)
		if (left >= 0) {
			result.push({ index: islandPositions[left].index, wave });
			left--;
		}
		// Add island to the right (if any)
		if (right < islandPositions.length) {
			result.push({ index: islandPositions[right].index, wave });
			right++;
		}
		wave++;
	}

	return result;
};
